/**
 * 67.61 (1), lane F's (b): the jungle's drawn layers hide and slow, or the drawn jungle is false cover. Its trunks are the
 * forest's (exact: cover, the walk's discs); what is drawn only — the understory of ferns and bushes, the canopy closing
 * overhead — is a per-cell depth field here, on weatherDepth's pattern (core/perception.ts): a line is sampled at a fixed
 * step, each sample keyed to its cell of the field's grid (JUNGLE_DEPTH.cellPeople across), and each cell's value made
 * once — its ground's radius when it lies in a jungle stand's disc (the stands as forestStands gives them, the jungle as
 * jungleAt says), 0 when not — and kept by the cell, so no value depends on which line asked first. A sample counts its
 * stretch × the layer's density where it lies: the understory from the ground to `understoryTopPeople`, the canopy between
 * `canopyFrom` and `canopyTo` of a tree's height. Off (no jungle share held, or no forest held): 0, nothing read.
 * Pure and deterministic: dmath, the forest's own functions, the ground at setup (the forest's basis).
 *
 * The share is the held forest's (`state.forest.jungle`), so the field and the held trees cannot disagree. The keep-out is
 * not in the memo's key: it is fixed per match (the forest's, at setup). Inside a fresh crater the understory's band reads
 * from the basis ground, as the trees stand on it (§5.17: the held trees are placed on the ground at setup).
 */
import type { GameState, Vec3 } from '../types';
// (the forest first: biomes.ts's own imports reach forest.ts, which reads BIOMES as it loads — core/foliage.ts imports this)
import { basisPlanet, forestStands, jungleAt, type ForestCandidate } from './forest';
import { BIOMES } from './biomes';
import { cellCentre, cellOf, cellsNear } from './cells';
import { keepOutOn } from './forestHeld';
import { dasin } from './dmath';
import { getSurfaceRadius } from './surface';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { simWork } from './workCounters';
import { NORMAL_TREE_PERSONS } from '../constants/action';

export const JUNGLE_DEPTH = {
  /** the field's cells, about this many people across (a cube-sphere grid of its own) */
  cellPeople: 2,
  /** the step along a line (people: a cell's, so each cell is read once), and the most samples a line takes */
  stepPeople: 2,
  maxSamples: 100,
  /** the understory: from the ground to this height (people), and its density (crown units a person of it: FOLIAGE_SIGHT's measure) */
  understoryTopPeople: 1.4,
  understory: 0.35,
  /** the canopy: between these shares of a tree's height (NORMAL_TREE_PERSONS), and its density */
  canopyFrom: 0.62,
  canopyTo: 1.0,
  canopy: 0.4,
};

/**
 * Per planet basis and key: the field's cells — one number each, the basis ground's radius at its centre when it lies in a
 * jungle stand's disc, 0 when not — the forest's cells any jungle stand reaches (the coarse pass: a line clear of them
 * reads nothing), and each forest cell's jungle stands.
 */
interface Memo { cells: Map<number, number>; coarse: Map<number, boolean>; stands: Map<number, ForestCandidate[]> }
const memo = new WeakMap<Float32Array, Map<string, Memo>>();
/** A memo's most cells before it starts afresh (the values are pure: starting afresh changes none). */
const MEMO_CAP = 200_000;

/** The angle between two unit directions (from the chord: dmath). */
function angleBetween(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return 2 * dasin(Math.min(1, Math.sqrt(dx * dx + dy * dy + dz * dz) / 2));
}
/** The widest stand's radius (people), read on first use (not as the module loads: see the imports). */
let maxStandRadius = 0;
const MAX_STAND_RADIUS_OF = () => maxStandRadius || (maxStandRadius = Math.max(...Object.values(BIOMES).map(b => b.radius)));

/** The jungle's layers a segment crosses, per field cell it samples in a jungle disc: the cell, the depth × density (world units). */
export function jungleCrossed(state: Pick<GameState, 'planets' | 'config' | 'forest'>, planetIndex: number, from: Vec3, to: Vec3): Array<{ cell: number; depth: number }> {
  const f = state.forest, share = f?.jungle ?? 0;
  if (!f || !(share > 0)) return [];
  const fp = f.planets.find(p => p.planetIndex === planetIndex), live = state.planets[planetIndex];
  if (!fp || !live) return [];
  const person = PERSON_BODY_HEIGHT * personSetOf(state as GameState).k;
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z, len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (!(len > 0)) return [];
  const planet = basisPlanet(live, fp.basis), P = person / planet.radius;
  const n = Math.max(8, Math.ceil(((Math.PI / 2) * planet.radius) / (JUNGLE_DEPTH.cellPeople * person)));
  let byKey = memo.get(fp.basis);
  if (!byKey) { byKey = new Map(); memo.set(fp.basis, byKey); }
  const key = `${fp.seed}|${person}|${n}|${share}|${f.density ?? ''}|${f.windSeed ?? ''}`;
  let m = byKey.get(key);
  if (!m || m.cells.size > MEMO_CAP) { m = { cells: new Map(), coarse: m?.coarse ?? new Map(), stands: m?.stands ?? new Map() }; byKey.set(key, m); }
  const cells = m.cells, standMemo = m.stands, keepOut = keepOutOn(f, planetIndex), treeH = NORMAL_TREE_PERSONS * person;
  const standsOf = (fc: number) => {
    let s = standMemo.get(fc);
    if (!s) { s = forestStands(planet, fp.seed, person, fp.n, fc, keepOut, f.density ?? 'flat', f.windSeed ?? '', share).filter(q => jungleAt(planet, fp.seed, person, q.dir, q.biome, share)); standMemo.set(fc, s); }
    return s;
  };
  // the coarse pass: the line at half a forest cell's step, each sample's forest cell — does a jungle stand reach it?
  const cellAngle = (Math.PI / 2) / fp.n, coarseStep = 0.5 * cellAngle * planet.radius;
  const coarseN = Math.max(1, Math.ceil(len / coarseStep));
  let any = false;
  for (let i = 0; i <= coarseN && !any; i++) {
    const t = i / coarseN, fc = cellOf(from.x + dx * t - live.position.x, from.y + dy * t - live.position.y, from.z + dz * t - live.position.z, fp.n);
    let reached = m.coarse.get(fc);
    if (reached === undefined) {
      // (a stand anchored within the cell's half-diagonal and the widest stand's radius of its centre)
      reached = false;
      const c = cellCentre(fc, fp.n);
      for (const nc of cellsNear(c, cellAngle + MAX_STAND_RADIUS_OF() * P, fp.n)) {
        if (standsOf(nc).some(s => angleBetween(c, s.dir) < cellAngle + BIOMES[s.biome!].radius * P)) { reached = true; break; }
      }
      m.coarse.set(fc, reached);
    }
    any = reached;
  }
  if (!any) return [];
  const samples = Math.max(1, Math.min(JUNGLE_DEPTH.maxSamples, Math.ceil(len / (JUNGLE_DEPTH.stepPeople * person))));
  const piece = len / samples, out: Array<{ cell: number; depth: number }> = [];
  for (let i = 0; i < samples; i++) {
    const t = (i + 0.5) / samples;
    const x = from.x + dx * t - live.position.x, y = from.y + dy * t - live.position.y, z = from.z + dz * t - live.position.z;
    const r = Math.sqrt(x * x + y * y + z * z);
    if (!(r > 0)) continue;
    const cell = cellOf(x, y, z, n);
    let ground = cells.get(cell);
    if (ground === undefined) {
      const d = cellCentre(cell, n);
      let jungle = false;
      // the stands round it (the forest's cells within the widest stand's reach), the first jungle disc it lies in
      outer: for (const fc of cellsNear(d, MAX_STAND_RADIUS_OF() * P, fp.n)) {
        for (const s of standsOf(fc)) {
          if (angleBetween(d, s.dir) < BIOMES[s.biome!].radius * P) { jungle = true; break outer; }
        }
      }
      ground = jungle ? getSurfaceRadius(planet, d) : 0;
      cells.set(cell, ground);
      simWork.jungleSamples++;
    }
    if (ground === 0) continue;
    const h = r - ground;
    const dens = h < 0 ? 0 : h <= JUNGLE_DEPTH.understoryTopPeople * person ? JUNGLE_DEPTH.understory
      : h >= JUNGLE_DEPTH.canopyFrom * treeH && h <= JUNGLE_DEPTH.canopyTo * treeH ? JUNGLE_DEPTH.canopy : 0;
    if (dens > 0) {
      const last = out[out.length - 1];
      if (last && last.cell === cell) last.depth += piece * dens; else out.push({ cell, depth: piece * dens });
    }
  }
  return out;
}

/** The jungle's depth × density along a segment (world units): the sum of jungleCrossed's. */
export function jungleDepth(state: Pick<GameState, 'planets' | 'config' | 'forest'>, planetIndex: number, from: Vec3, to: Vec3): number {
  let sum = 0;
  for (const x of jungleCrossed(state, planetIndex, from, to)) sum += x.depth;
  return sum;
}
